/* =========================================================================
   Penny Premium 3D — cart-drawer.js
   Cart drawer + shared cart-mutation engine (drawer AND cart page).

   Architecture: the drawer/page markup is server-rendered Liquid only.
   Every mutation POSTs the Cart AJAX API with the `sections` parameter
   and swaps the returned HTML — no client templating of line items.
   Optimistic UI = disabled controls while a request is in flight.

   Events:
     consumes 'cart-drawer:open'  → opens (also fired by cart.js on add)
     consumes 'cart:sections'     → swaps server-rendered section HTML
     emits    'cart-drawer:close' → on every close (sticky ATC re-shows)
     emits    'cart:updated'      → after each mutation (header bubble)
   ========================================================================= */

const penny = window.penny;
const motionFull = () => document.documentElement.classList.contains('motion-full');
const rootRoute = window.Shopify?.routes?.root ?? '/';

const drawer = document.querySelector('[data-cart-drawer]');
const scrim = document.querySelector('[data-cart-drawer-scrim]');
let isOpen = false;
let crossSellCache = { productId: null, html: null };

const sectionIds = () =>
  Array.from(document.querySelectorAll('[data-cart-rerender]'))
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);

const markRevealed = (scope) => {
  scope.querySelectorAll('.reveal:not(.is-revealed)').forEach((el) => el.classList.add('is-revealed'));
};

/* --- Open / close --------------------------------------------------------- */

function animateShippingBar() {
  const fill = drawer?.querySelector('[data-shipping-fill]');
  if (!fill || !motionFull()) return;
  const target = fill.style.width;
  fill.classList.add('no-transition');
  fill.style.width = '0%';
  void fill.offsetWidth;
  fill.classList.remove('no-transition');
  fill.style.width = target;
}

function updateWishlistCta() {
  const cta = document.querySelector('[data-wishlist-cta]');
  if (cta && penny.wishlist?.list().length > 0) cta.removeAttribute('hidden');
}

function openDrawer() {
  if (!drawer || isOpen) return;
  isOpen = true;

  // Never stack dialogs: close quick view / lightboxes / hotspot popovers
  document.querySelectorAll('dialog[open]').forEach((d) => d.close());
  document.querySelectorAll('[data-hotspot-popover]:not([hidden])').forEach((p) => p.setAttribute('hidden', ''));

  drawer.removeAttribute('hidden');
  scrim?.removeAttribute('hidden');
  requestAnimationFrame(() => {
    drawer.classList.add('is-open');
    scrim?.classList.add('is-open');
  });
  document.body.classList.add('scroll-lock');
  penny.trapFocus(drawer, drawer.querySelector('[data-cart-drawer-close]'));
    if (document.getElementById('MainContent')) document.getElementById('MainContent').inert = true;
  updateWishlistCta();

  // Choreography: drawer settles, then (≈90ms later) the bar fills
  const settle = () => window.setTimeout(animateShippingBar, 90);
  if (!motionFull()) settle();
  else drawer.addEventListener('transitionend', settle, { once: true });

  loadCrossSell();
}

function closeDrawer() {
  if (!drawer || !isOpen) return;
  isOpen = false;
  drawer.classList.remove('is-open');
  scrim?.classList.remove('is-open');
  document.body.classList.remove('scroll-lock');
  penny.releaseFocus();
  penny.emit('cart-drawer:close');
    if (document.getElementById('MainContent')) document.getElementById('MainContent').inert = false;
  const done = () => {
    drawer.setAttribute('hidden', '');
    scrim?.setAttribute('hidden', '');
  };
  if (!motionFull()) done();
  else drawer.addEventListener('transitionend', done, { once: true });
}

penny.on('cart-drawer:open', openDrawer);
scrim?.addEventListener('click', closeDrawer);
document.addEventListener('click', (event) => {
  if (event.target.closest('[data-cart-drawer-close]')) closeDrawer();
});
drawer?.addEventListener('keydown', (event) => {
  if (event.key === 'Escape') closeDrawer();
});

/* --- Section swaps -------------------------------------------------------- */

function applySections(sections) {
  if (!sections) return;

  // Remember where focus was (a stepper button gets replaced by the swap)
  const active = document.activeElement;
  const focusDesc =
    active && (drawer?.contains(active) || active.closest('[data-cart-rerender]'))
      ? {
          lineKey: active.closest('[data-line-key]')?.dataset.lineKey,
          change: active.dataset.cartQtyChange,
        }
      : null;

  Object.entries(sections).forEach(([, html]) => {
    if (!html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');

    const freshDrawer = doc.querySelector('[data-cart-drawer]');
    if (freshDrawer && drawer) {
      drawer.replaceChildren(...freshDrawer.childNodes);
      return;
    }

    const freshMain = doc.querySelector('[data-cart-main]');
    const currentMain = document.querySelector('[data-cart-main]');
    if (freshMain && currentMain) {
      currentMain.replaceChildren(...freshMain.childNodes);
      markRevealed(currentMain);
    }
  });

  applyDiscountUI();
  updateWishlistCta();
  reinjectCrossSell();

  if (focusDesc?.lineKey) {
    const row = document.querySelector(`[data-line-key="${focusDesc.lineKey}"]`);
    const target = focusDesc.change
      ? row?.querySelector(`[data-cart-qty-change="${focusDesc.change}"]`)
      : row?.querySelector('[data-cart-qty-input]');
    (target || drawer?.querySelector('[data-cart-drawer-close]'))?.focus();
  }
}

penny.on('cart:sections', (event) => applySections(event.detail));

/* --- Mutations ------------------------------------------------------------ */

let mutating = false;

async function changeLine(lineKey, quantity) {
  if (mutating) return;
  mutating = true;

  const row = document.querySelectorAll(`[data-line-key="${lineKey}"]`);
  row.forEach((r) => {
    r.querySelectorAll('button, input, a').forEach((el) => (el.disabled = true));
    r.classList.add('is-busy');
  });

  try {
    const response = await fetch(`${rootRoute}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        id: lineKey,
        quantity,
        sections: sectionIds().join(','),
        sections_url: window.location.pathname,
      }),
    });
    const data = await response.json();

    if (!response.ok) {
      const slot = document.querySelector(`[data-line-key="${lineKey}"] [data-line-error]`);
      if (slot) slot.textContent = data.description || data.message || '';
      row.forEach((r) => {
        r.querySelectorAll('button, input, a').forEach((el) => (el.disabled = false));
        r.classList.remove('is-busy');
      });
      return;
    }

    applySections(data.sections);
    penny.emit('cart:updated', data);
  } catch (_) {
    window.location.reload();
  } finally {
    mutating = false;
  }
}

document.addEventListener('click', (event) => {
  const stepButton = event.target.closest('[data-cart-qty-change]');
  if (stepButton) {
    const key = stepButton.dataset.lineKey;
    const input = document.querySelector(`[data-cart-qty-input][data-line-key="${key}"]`);
    const delta = Number(stepButton.dataset.cartQtyChange);
    input?.classList.add(delta > 0 ? 'tick-up' : 'tick-down');
    const next = Math.max(0, Number(input?.value || 1) + delta);
    changeLine(key, next);
    return;
  }

  const remove = event.target.closest('[data-cart-remove]');
  if (remove) {
    event.preventDefault();
    changeLine(remove.dataset.lineKey, 0);
  }
});

document.addEventListener('change', (event) => {
  const input = event.target.closest('[data-cart-qty-input]');
  if (input) changeLine(input.dataset.lineKey, Math.max(0, Number(input.value || 0)));
});

/* --- Order note (debounced, "Saved ✓") ------------------------------------ */

let noteTimer = null;

document.addEventListener('input', (event) => {
  const note = event.target.closest('[data-cart-note]');
  if (!note) return;
  window.clearTimeout(noteTimer);
  noteTimer = window.setTimeout(async () => {
    try {
      await fetch(`${rootRoute}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: note.value }),
      });
      const status = note.parentElement.querySelector('[data-note-status]');
      if (status) {
        status.textContent = status.dataset.savedLabel || note.dataset.savedLabel;
        window.setTimeout(() => (status.textContent = ''), 2000);
      }
    } catch (_) {
      /* saved with the next full-form submit instead */
    }
  }, 600);
});

/* --- Discount code (applied at checkout via ?discount=) ------------------- */

const DISCOUNT_KEY = 'penny:discount';

const getDiscount = () => {
  try {
    return sessionStorage.getItem(DISCOUNT_KEY) || '';
  } catch (_) {
    return '';
  }
};

function applyDiscountUI() {
  const code = getDiscount();
  document.querySelectorAll('[data-discount-field]').forEach((field) => field.toggleAttribute('hidden', !!code));
  document.querySelectorAll('[data-discount-pill]').forEach((pill) => pill.toggleAttribute('hidden', !code));
  document.querySelectorAll('[data-discount-code]').forEach((slot) => (slot.textContent = code));
  document.querySelectorAll('a[data-checkout]').forEach((link) => {
    link.setAttribute('href', code ? `/checkout?discount=${encodeURIComponent(code)}` : '/checkout');
  });
}

document.addEventListener('click', (event) => {
  if (event.target.closest('[data-discount-apply]')) {
    const input = event.target.closest('.cart-extras__discount')?.querySelector('[data-discount-input]');
    const code = input?.value.trim();
    if (!code) return;
    try {
      sessionStorage.setItem(DISCOUNT_KEY, code);
    } catch (_) {
      /* fine */
    }
    applyDiscountUI();
    return;
  }

  if (event.target.closest('[data-discount-remove]')) {
    try {
      sessionStorage.removeItem(DISCOUNT_KEY);
    } catch (_) {
      /* fine */
    }
    applyDiscountUI();
  }
});

/* --- Checkout guard (terms + page-form discount carry) --------------------- */

document.addEventListener('click', (event) => {
  const checkout = event.target.closest('[data-checkout]');
  if (!checkout) return;

  const summary = checkout.closest('[data-cart-summary]');
  const terms = summary?.querySelector('[data-cart-terms]');
  const error = summary?.querySelector('[data-terms-error]');

  if (terms && !terms.checked) {
    event.preventDefault();
    error?.removeAttribute('hidden');
    terms.focus();
    return;
  }
  error?.setAttribute('hidden', '');

  // Page form submit button: carry the discount code to checkout
  const code = getDiscount();
  if (code && checkout.tagName === 'BUTTON') {
    event.preventDefault();
    window.location.href = `/checkout?discount=${encodeURIComponent(code)}`;
  }
});

/* --- Cross-sell ("Complete your order") ------------------------------------ */

function reinjectCrossSell() {
  const container = document.querySelector('[data-cart-cross-sell]');
  if (!container) return;
  if (crossSellCache.html && crossSellCache.productId === container.dataset.firstProduct) {
    container.querySelector('[data-cross-sell-slot]').innerHTML = crossSellCache.html;
    markRevealed(container);
    container.removeAttribute('hidden');
  } else if (isOpen) {
    loadCrossSell();
  }
}

async function loadCrossSell() {
  const container = document.querySelector('[data-cart-cross-sell]');
  if (!container) return;
  const productId = container.dataset.firstProduct;

  if (crossSellCache.productId === productId && crossSellCache.html) {
    reinjectCrossSell();
    return;
  }

  const attempts = [
    `${rootRoute}recommendations/products?section_id=cart-cross-sell&product_id=${productId}&limit=12&intent=complementary`,
    `${rootRoute}recommendations/products?section_id=cart-cross-sell&product_id=${productId}&limit=12&intent=related`,
    `${rootRoute}?section_id=cart-cross-sell`,
  ];

  for (const url of attempts) {
    try {
      const response = await fetch(url);
      if (!response.ok) continue;
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const grid = doc.querySelector('[data-cross-sell-loaded]');
      if (grid) {
        crossSellCache = { productId, html: grid.outerHTML };
        reinjectCrossSell();
        return;
      }
    } catch (_) {
      /* try the next source */
    }
  }
  container.setAttribute('hidden', '');
}

/* --- Boot ------------------------------------------------------------------ */
applyDiscountUI();
updateWishlistCta();
if (document.querySelector('[data-cart-main]')) loadCrossSell();
